import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { FileText } from 'lucide-react';
import { clsx } from '../../../ds/clsx';
import { Button, Icon, Toggle, useToast } from '../../../ds/components';
import { errorMessage } from '../../../lib/api';
import { queryRoots } from '../../../lib/events';
import { portalKeys, setDocumentVisible, type StaffPortalCard } from '../portal-api';
import type { DocumentRow } from '../types';
import styles from './portal.module.css';

export interface PortalDocumentsSidebarProps {
  documents: StaffPortalCard['documents'];
  /** „Weiteres Dokument anfordern" → the request editor with channel „nur Portal". */
  onRequest: () => void;
}

function requestsText(requests: StaffPortalCard['documents']['openPortalRequests']): string {
  if (requests.length === 0)
    return 'Keine offene Anforderung. Angeforderte Unterlagen erscheinen beim Kunden als Aufgabe.';
  const titles = requests.map((request) => request.title).join(', ');
  return requests.length === 1
    ? `Eine offene Anforderung: ${titles}. Erscheint beim Kunden als Aufgabe.`
    : `${requests.length} offene Anforderungen: ${titles}. Erscheinen beim Kunden als Aufgabe.`;
}

/** Mandantenportal 29 sidebar: „Vom Kunden hochgeladen" and „Anforderung ins Portal". */
export function PortalDocumentsSidebar({ documents, onRequest }: PortalDocumentsSidebarProps) {
  return (
    <aside className={styles.sidebar} aria-label="Portal">
      <section className={styles.sideCard} aria-label="Vom Kunden hochgeladen">
        <div className={styles.eyebrow}>Vom Kunden hochgeladen</div>
        {documents.customerUploads.length > 0 ? (
          <ul className={styles.uploads}>
            {documents.customerUploads.map((upload) => (
              <li key={upload.id} className={styles.upload}>
                <Icon icon={FileText} size={18} />
                <div className={styles.uploadText}>
                  <div className={styles.uploadName}>{upload.name}</div>
                  <div className={styles.uploadMeta}>{upload.meta}</div>
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <p className={styles.sideEmpty}>Noch nichts hochgeladen.</p>
        )}
        <div className={styles.sideNote}>
          Hochgeladene Dateien sind für den Kunden immer sichtbar und lassen sich nicht ausblenden.
        </div>
      </section>
      <section className={styles.sideCard} aria-label="Anforderung ins Portal">
        <div className={styles.eyebrow}>Anforderung ins Portal</div>
        <p className={styles.sideText}>{requestsText(documents.openPortalRequests)}</p>
        <Button className={styles.sideButton} onClick={onRequest}>
          Weiteres Dokument anfordern
        </Button>
      </section>
    </aside>
  );
}

/**
 * „Im Portal sichtbar" of a document row (Portal 29): the 34 × 20 switch (on accent, off sand, locked at .45 for
 * internal documents; customer uploads stay on) and `visibleSinceLabel` → `PATCH /documents/:id`. Request rows
 * show the label only; a 409 of the server appears as a toast.
 */
export function VisibilityCell({ row, contactId }: { row: DocumentRow; contactId: string }) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [busy, setBusy] = useState(false);
  const alwaysOn = row.visibilityLocked && row.visibleToCustomer;
  const on = row.visibleToCustomer && !row.visibilityLocked;

  async function change(next: boolean) {
    setBusy(true);
    try {
      await setDocumentVisible(row.id, next);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [...queryRoots.documents] }),
        queryClient.invalidateQueries({ queryKey: portalKeys.card(contactId) }),
      ]);
    } catch (error) {
      toast({ message: errorMessage(error) });
    } finally {
      setBusy(false);
    }
  }

  return (
    <span className={styles.visibility}>
      {row.kind === 'document' ? (
        <span className={clsx(styles.toggleTarget, alwaysOn && styles.alwaysOn)}>
          <Toggle
            size="large"
            label={`${row.name} im Portal sichtbar`}
            labelHidden
            checked={row.visibleToCustomer}
            locked={row.visibilityLocked}
            disabled={busy}
            onChange={(next) => void change(next)}
          />
        </span>
      ) : null}
      <span className={clsx(styles.visibleLabel, on && styles.visibleLabelOn)}>{row.visibleSinceLabel}</span>
    </span>
  );
}
